import React, { useState, useEffect } from 'react';

/**
 * Overlay de Carga Psicológico para Inspector (con contador en segundos en tiempo real).
 * Bloquea la interacción del usuario y muestra animación rápida con segundero transparente.
 */
export const InspectorLoadingOverlay = ({ active, message = "Procesando información...", onCancel }) => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    let timer = null;
    if (active) {
      setSeconds(0);
      timer = setInterval(() => {
        setSeconds(prev => prev + 1);
      }, 1000);
    } else {
      setSeconds(0);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [active]);

  if (!active) return null;

  // Formatear segundos en min:seg si excede 60s
  const formatTime = (totalSecs) => {
    if (totalSecs < 60) return `${totalSecs}s`;
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins}m ${secs < 10 ? '0' : ''}${secs}s`;
  };

  const handleCancelClick = () => {
    setSeconds(0);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(15, 23, 42, 0.88)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      zIndex: 999999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      boxSizing: 'border-box',
      userSelect: 'none',
      WebkitUserSelect: 'none'
    }}>
      {/* Contenedor central flotante */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '24px',
        padding: '36px 28px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '340px',
        width: '90%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 30px rgba(59, 130, 246, 0.2)',
        textAlign: 'center'
      }}>
        {/* Spinner rápido con doble anillo */}
        <div style={{ position: 'relative', width: '72px', height: '72px', marginBottom: '20px' }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '4px solid rgba(255, 255, 255, 0.1)',
            borderTopColor: '#3b82f6',
            borderRightColor: '#10b981',
            animation: 'fastSpin 0.7s linear infinite'
          }} />
          <div style={{
            position: 'absolute',
            inset: '8px',
            borderRadius: '50%',
            border: '3px solid rgba(255, 255, 255, 0.05)',
            borderBottomColor: '#f59e0b',
            animation: 'reverseFastSpin 0.5s linear infinite'
          }} />
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px'
          }}>
            ⚡
          </div>
        </div>

        {/* Mensaje descriptivo */}
        <div style={{
          color: '#f8fafc',
          fontWeight: 700,
          fontSize: '15px',
          lineHeight: '1.4',
          marginBottom: '12px',
          letterSpacing: '0.2px'
        }}>
          {message}
        </div>

        {/* Contador de tiempo transcurrido en vivo */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.8)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px',
          padding: '6px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#60a5fa',
          fontWeight: 800,
          fontSize: '14px',
          letterSpacing: '0.5px'
        }}>
          <span style={{ fontSize: '12px' }}>⏱️ Tiempo transcurrido:</span>
          <span style={{ color: '#34d399', fontSize: '16px', fontFamily: 'monospace' }}>
            {formatTime(seconds)}
          </span>
        </div>

        <div style={{
          marginTop: '16px',
          fontSize: '11px',
          color: '#94a3b8',
          lineHeight: '1.3'
        }}>
          🔒 No cierres ni cambies de pantalla durante la sincronización.
        </div>

        {/* Botón de escape / cancelación si supera 10 segundos por mala señal */}
        {seconds >= 10 && (
          <button
            type="button"
            onClick={handleCancelClick}
            style={{
              marginTop: '20px',
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid #ef4444',
              color: '#fca5a5',
              padding: '10px 18px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s',
              width: '100%'
            }}
          >
            ❌ Cancelar y Desbloquear
          </button>
        )}
      </div>

      {/* Animaciones CSS inyectadas de alta velocidad */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fastSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes reverseFastSpin {
          0% { transform: rotate(360deg); }
          100% { transform: rotate(0deg); }
        }
      ` }} />
    </div>
  );
};

export default InspectorLoadingOverlay;
